import { wrapBusy } from '@/shared/domain-base.component';
import { StringFns } from '@/shared/string-fns';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { Email, JoHeader, Manifest, ManifestGroup } from 'app/model/entities/entity-model';
import { DocSubmitTypeEnum } from 'app/model/entities/enums';
import { DateFns } from 'app/shared/date-fns';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { EmailService } from 'app/shared/email.service';
import { UtilFns } from 'app/shared/util-fns';
import { environment } from 'environments/environment';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

export interface ManifestPrintDialogParams extends DialogParams {
  manifest: Manifest;
  manifestGroup: ManifestGroup;
  canPreview: boolean;
}

@Component({
  templateUrl: './manifest-print-dialog.component.html',
  standalone: false
})
export class ManifestPrintDialogComponent extends DialogBaseComponent<ManifestPrintDialogParams> {

  manifest: Manifest;
  manifestGroup: ManifestGroup;
  joHeader: JoHeader;
  emailAddress: string;
  email: Email;
  pdfurl: string;
  canPreview: boolean;
  isPrinting: boolean;
  isAccounted: boolean;
  isEmailed = false;

  constructor(public config: DynamicDialogConfig<ManifestPrintDialogParams>, public dialogRef: DynamicDialogRef,
    public domainService: DomainService, private emailService: EmailService, private router: Router
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      header: 'Print/Email Manifest',
      width: '800px',
      height: '340px'
    });
    this.prepare();
  }

  prepare() {
    this.manifest = this.params.manifest;
    this.manifestGroup = this.params.manifestGroup;
    this.canPreview = this.params.canPreview;

    this.joHeader = this.manifest.joHeaders[0];
    // Same source as an invoice email (printable-invoice emailInvoice, invoice-processing.service):
    // the Job Order's billing email, else the Account's invoice email. Either may hold several
    // addresses separated by semicolons; the server splits them (MailgunSender.CreateMailAddresses).
    this.emailAddress = this.joHeader?.billingEmail?.trim() || this.joHeader?.account?.invoiceEmail?.trim() || '';
    this.isPageReady = true;
  }

  // Manifests follow the account's invoice Doc Submit Type. An Email account posts only when the
  // server confirms delivery, so Print is blocked there to keep a printed copy from posting it.
  canPrint(): boolean {
    return this.joHeader?.account?.invoiceDocSubmitTypeId !== DocSubmitTypeEnum.Email;
  }

  canSendEmail(): boolean {
    return !this.isEmailed && UtilFns.validateMultipleEmail(this.emailAddress?.trim());
  }

  @wrapBusy
  async sendEmail() {
    if (!this.canSendEmail()) return;
    this.isEmailed = true;

    const status = await this.emailService.sendManifestEmail(this.manifestGroup.id, this.emailAddress.trim());

    if (status === "accepted" || status === "stored") {
      this.toastr.info(status + "; checking for delivery", "Email Status");
      setTimeout(() => this.getEmailStatus(), 3000);
      await this.finalizeManifestGroup();
    } else {
      this.isEmailed = false;
      this.toastr.error(status, "Email Status");
    }
  }

  async getEmailStatus(showToast = true) {
    const email = await this.emailService.getManifestEmailStatus(this.manifestGroup.id);
    if (email) {
      this.email = email;
      if (showToast) {
        this.toastr.info(email.status, "Email Status");
      }
    }
  }

  async printDocument() {
    if (!this.canPrint()) return;
    this.pdfurl = environment.apiRoot + 'api/pdf/pdffromurl/Manifest' + this.manifest.id + '?url=' + UtilFns.getBaseUrl() +
      'printable-manifest-group/' + this.manifestGroup.id;

    window.open(this.pdfurl, '_blank');

    if (this.manifestGroup.sentDate == null) {
      this.manifestGroup.sentDate = DateFns.startOfDay(new Date());
    }
    await this.finalizeManifestGroup();
  }

  private async finalizeManifestGroup() {
    if (this.dbSaveService.hasChanges()) {
      await this.dbSaveService.saveChanges();
    }
    await this.savePdfToDrive();
    await this.dbSaveService.updateManifestAndRelated(this.manifest);
  }

  private async savePdfToDrive() {
    const accountName = StringFns.stripInvalidFilenameChars(this.joHeader?.account?.accountName || 'Default');
    const manifestName = StringFns.stripInvalidFilenameChars(this.manifest.manifestName || 'Default');

    const body = {
      title: 'Manifest' + this.manifest.id,
      pdfUrl: UtilFns.getBaseUrl() + 'printable-manifest-group/' + this.manifestGroup.id,
      folderNames: [accountName, manifestName],
    };
    const url = environment.apiRoot + 'api/pdf/SavePdfToDrive';
    await this.dbSaveService.postData<string>(url, body, { responseType: 'text' as 'json' });
  }

  showPreview() {
    let url = '/printable-manifest-group/' + this.manifestGroup.id;
    this.router.navigateByUrl(url);
    this.close();
  }

  close() {
    this.dialogRef.close(null);
  }

}
